// Copyright 2025-2026 Florian Gallon. All Rights reserved.
/**
 * Protected Baker Route Component
 *
 * Wraps baker-only routes to enforce subscription access control.
 * Bakers must have either:
 * - Active or trialing subscription
 * - Grandfathered status
 * - Be within 7-day grace period after registration
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isGrandfathered, hasActiveSubscription, getGracePeriodInfo } from '../utils/stripeUtils';

export default function ProtectedBakerRoute({
  currentUser,
  userProfile,
  currentBakery,
  isAdminImpersonating = false,
  children
}) {
  const location = useLocation();

  // Check authentication
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin impersonation bypass — admin stays authenticated as admin,
  // skip role and subscription checks
  if (isAdminImpersonating) {
    if (location.pathname.includes('/settings')) {
      return (
        <div className="p-8 text-center text-[var(--text-secondary)]">
          Settings cannot be modified during admin impersonation.
        </div>
      );
    }
    return children;
  }

  // Check baker role
  if (userProfile?.role !== 'baker') {
    return <Navigate to="/login" replace />;
  }

  // Allow access to settings page (so baker can subscribe)
  const isSettingsPage = location.pathname.includes('/settings');
  if (isSettingsPage) {
    return children;
  }

  // Check subscription, grace period, or grandfathered status
  // Must check BOTH database status AND actual date calculation
  const gracePeriodInfo = getGracePeriodInfo(currentBakery);
  const isInGracePeriod = currentBakery?.subscriptionStatus === 'grace_period' && gracePeriodInfo?.isInGracePeriod;
  const hasAccess = isGrandfathered(currentBakery) || hasActiveSubscription(currentBakery) || isInGracePeriod;

  if (!hasAccess) {
    // Redirect to subscription settings with message
    const subdomain = currentBakery?.subdomain || 'shop';
    return <Navigate
      to={`/${subdomain}/settings?tab=subscription&reason=no-access`}
      replace
    />;
  }

  return children;
}
